import type { ReactNode } from 'react';
import {
  Input,
  Select,
  type SelectOption,
} from '@viquard/doc-ui/components';
import type {
  CalendarLocationType,
  CalendarMeetingProvider,
} from '../../utils/location.js';
import {
  MEETING_PROVIDER_VALUES,
  providerLabel,
} from '../../utils/location.js';
import { useCalendarMessages } from '../../i18n.js';
import { FieldRow, Section } from './layout.js';

export function LocationSection(props: {
  readonly locationType: CalendarLocationType | null;
  readonly meetingProvider: CalendarMeetingProvider;
  readonly meetingDetail: string;
  readonly locationText: string;
  readonly onLocationTypeChange: (value: CalendarLocationType | null) => void;
  readonly onMeetingProviderChange: (value: CalendarMeetingProvider) => void;
  readonly onMeetingDetailChange: (value: string) => void;
  readonly onLocationTextChange: (value: string) => void;
}): ReactNode {
  const messages = useCalendarMessages();
  const locationOptions: SelectOption[] = [
    { value: '__none__', label: messages.composerLocationNone },
    { value: 'online', label: messages.composerLocationOnline },
    { value: 'inPerson', label: messages.composerLocationInPerson },
    { value: 'custom', label: messages.composerLocationCustom },
  ];
  const providerOptions: SelectOption<CalendarMeetingProvider>[] = MEETING_PROVIDER_VALUES.map((value) => ({
    value,
    label: providerLabel(value, messages),
  }));
  return (
    <Section title={messages.composerSectionLocation}>
      <div className="vq-cal__composer-grid vq-cal__composer-grid--2">
        <FieldRow
          label={messages.composerLocationTypeLabel}
          filled={props.locationType !== null}
        >
          <Select
            options={locationOptions}
            value={props.locationType ?? '__none__'}
            onValueChange={(value) => {
              props.onLocationTypeChange(
                value === '__none__' ? null : value as CalendarLocationType,
              );
            }}
            aria-label={messages.composerLocationTypeLabel}
            size="md"
          />
        </FieldRow>
        {props.locationType === 'online' ? (
          <FieldRow label={messages.composerMeetingProviderLabel} filled>
            <Select<CalendarMeetingProvider>
              options={providerOptions}
              value={props.meetingProvider}
              onValueChange={props.onMeetingProviderChange}
              aria-label={messages.composerMeetingProviderLabel}
              size="md"
            />
          </FieldRow>
        ) : null}
        {props.locationType === 'online' ? (
          <FieldRow
            label={messages.composerMeetingDetailLabel}
            filled={props.meetingDetail.trim().length > 0}
          >
            <Input
              value={props.meetingDetail}
              onChange={(event) => { props.onMeetingDetailChange(event.target.value); }}
              placeholder={messages.composerMeetingDetailPlaceholder}
              size="md"
            />
          </FieldRow>
        ) : null}
        {props.locationType === 'inPerson' || props.locationType === 'custom' ? (
          <FieldRow
            label={messages.composerLocationTextLabel}
            filled={props.locationText.trim().length > 0}
          >
            <Input
              value={props.locationText}
              onChange={(event) => { props.onLocationTextChange(event.target.value); }}
              placeholder={
                props.locationType === 'inPerson'
                  ? messages.composerLocationInPersonPlaceholder
                  : messages.composerLocationCustomPlaceholder
              }
              size="md"
            />
          </FieldRow>
        ) : null}
      </div>
    </Section>
  );
}
